import { Fragment, h } from "preact";
import { useLayoutEffect, useRef, useState } from "preact/hooks";

import { FONT_FAMILY } from "../../../../foundation/utils/font";
import { getLineByWords, getLineWithLeadingEllipsis } from "../../../../foundation/utils/getTruncatedName";

import HighlightText from "../../HighlightText";
import { PopoverPosition } from "../../Popover";
import Tooltip from "../../Tooltip";

import styles from "./ScreenCardName.module.css";

const FONT = `400 13px ${FONT_FAMILY}`;

interface Props {
    class?: string;
    name: string;
    filterValue?: string;
}

function ScreenCardName({
    class: cssClass,
    name,
    filterValue
}: Props) {
    const ref = useRef<HTMLDivElement>(null);

    const [lines, setLines] = useState<{
        first: string;
        second?: string;
        isTruncated: boolean;
    }>({
        first: name,
        isTruncated: false
    });

    useLayoutEffect(() => {
        const element = ref.current;

        if (!element || !element.offsetWidth) {
            return;
        }

        function recalculate() {
            const maxWidth = element.offsetWidth;

            const firstLine = getLineByWords(name, FONT, maxWidth);
            if (firstLine === name) {
                setLines({ first: name, isTruncated: false });

                return;
            }

            const rest = name.substring(firstLine.length).trimStart();
            const secondLine = getLineWithLeadingEllipsis(rest, FONT, maxWidth);

            setLines({ first: firstLine, second: secondLine, isTruncated: secondLine !== rest });
        }

        recalculate();

        const resizeObserver = new ResizeObserver(recalculate);
        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, [name, lines.isTruncated]);

    function renderLines() {
        return (
            <>
                <HighlightText
                    class="body-1"
                    text={lines.first}
                    filterValue={filterValue}
                    enableWordSearch/>
                {lines.second !== undefined && (
                    <HighlightText
                        class="body-1"
                        text={lines.second}
                        filterValue={filterValue}
                        enableWordSearch/>
                )}
            </>
        );
    }

    if (lines.isTruncated) {
        return (
            <Tooltip
                class={styles.tooltip}
                targetClass={cssClass}
                title={name}
                position={PopoverPosition.Bottom}>
                <div ref={ref}>
                    {renderLines()}
                </div>
            </Tooltip>
        );
    }

    return (
        <div ref={ref} class={cssClass}>
            {renderLines()}
        </div>
    );
}

export default ScreenCardName;
